import React, {useMemo, useRef, useCallback} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {MinimalCutSet} from '../../../../utils/minimal-cut-sets';

interface CoOccurrenceHeatmapProps {
    cutSets: MinimalCutSet[];
    nodeNameMap: Map<number, string>;
}

const CELL_SIZE = 28;

function getColor(value: number, max: number): string {
    if (value === 0 || max === 0) return '#f5f5f5';
    const logVal = Math.log(value + 1);
    const logMax = Math.log(max + 1);
    const intensity = logVal / logMax;
    const r = 211;
    const g = Math.round(47 + (1 - intensity) * 160);
    const b = Math.round(47 + (1 - intensity) * 160);
    return `rgb(${r}, ${g}, ${b})`;
}

function getTextColor(value: number, max: number): string {
    if (value === 0 || max === 0) return '#999';
    const logVal = Math.log(value + 1);
    const logMax = Math.log(max + 1);
    const intensity = logVal / logMax;
    return intensity >= 0.4 ? '#fff' : '#333';
}

function truncateName(name: string, maxLen: number): string {
    return name.length > maxLen ? name.substring(0, maxLen - 2) + '..' : name;
}

const CoOccurrenceHeatmap: React.FC<CoOccurrenceHeatmapProps> = ({ cutSets, nodeNameMap }) => {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const { matrix, baseEventIds, maxVal } = useMemo(() => {
        const idSet = new Set<number>();
        for (const cs of cutSets) {
            if (cs.order >= 2) {
                for (const id of cs.baseEventIds) {
                    idSet.add(id);
                }
            }
        }

        const ids = Array.from(idSet).sort((a, b) =>
            (nodeNameMap.get(a) || '').localeCompare(nodeNameMap.get(b) || '')
        );

        const n = ids.length;
        const idIndex = new Map<number, number>();
        ids.forEach((id, i) => idIndex.set(id, i));

        const mat: number[][] = Array.from({ length: n }, () => new Array(n).fill(0));
        for (const cs of cutSets) {
            if (cs.order < 2) continue;
            const members = Array.from(cs.baseEventIds).filter((id) => idIndex.has(id));
            for (let i = 0; i < members.length; i++) {
                for (let j = i + 1; j < members.length; j++) {
                    const ri = idIndex.get(members[i])!;
                    const rj = idIndex.get(members[j])!;
                    mat[ri][rj]++;
                    mat[rj][ri]++;
                }
            }
        }

        for (const cs of cutSets) {
            for (const id of cs.baseEventIds) {
                const idx = idIndex.get(id);
                if (idx !== undefined) mat[idx][idx]++;
            }
        }

        let mv = 0;
        for (let i = 0; i < n; i++) {
            for (let j = 0; j < n; j++) {
                if (i !== j && mat[i][j] > mv) mv = mat[i][j];
            }
        }

        return { matrix: mat, baseEventIds: ids, maxVal: mv };
    }, [cutSets, nodeNameMap]);

    const n = baseEventIds.length;

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLTableElement>): void => {
        const tooltip = tooltipRef.current;
        const container = containerRef.current;
        if (!tooltip || !container) return;

        const target = e.target as HTMLElement;
        const td = target.closest('td[data-row][data-col]') as HTMLElement | null;
        if (!td) {
            tooltip.style.display = 'none';
            return;
        }

        const row = parseInt(td.dataset.row!, 10);
        const col = parseInt(td.dataset.col!, 10);
        const val = matrix[row][col];
        const rowName = nodeNameMap.get(baseEventIds[row]) || `ID:${baseEventIds[row]}`;
        const colName = nodeNameMap.get(baseEventIds[col]) || `ID:${baseEventIds[col]}`;

        tooltip.textContent = row === col
            ? `${rowName}: appears in ${val} cut set${val !== 1 ? 's' : ''}`
            : `${rowName} & ${colName}: co-occur in ${val} cut set${val !== 1 ? 's' : ''}`;

        const containerRect = container.getBoundingClientRect();
        tooltip.style.display = 'block';
        tooltip.style.left = `${e.clientX - containerRect.left + container.scrollLeft + 12}px`;
        tooltip.style.top = `${e.clientY - containerRect.top + container.scrollTop - 28}px`;
    }, [matrix, baseEventIds, nodeNameMap]);

    const handleMouseLeave = useCallback((): void => {
        if (tooltipRef.current) tooltipRef.current.style.display = 'none';
    }, []);

    // Pre-build the HTML string for the table body to avoid N×N React elements
    const tableHtml = useMemo((): string => {
        const rows: string[] = [];

        // Header row
        const headerCells = ['<th style="min-width:140px"></th>'];
        for (let j = 0; j < n; j++) {
            const name = nodeNameMap.get(baseEventIds[j]) || `ID:${baseEventIds[j]}`;
            const display = truncateName(name, 18);
            headerCells.push(
                `<th style="width:${CELL_SIZE}px;height:120px;padding:0;vertical-align:bottom">` +
                `<div style="transform:rotate(-60deg);transform-origin:bottom left;white-space:nowrap;font-size:10px;color:#333;width:${CELL_SIZE}px;margin-left:${CELL_SIZE / 2 + 4}px;margin-bottom:4px" title="${name.replace(/"/g, '&quot;')}">${display}</div>` +
                '</th>'
            );
        }
        rows.push(`<thead><tr>${headerCells.join('')}</tr></thead>`);

        // Body rows
        const bodyRows: string[] = [];
        for (let i = 0; i < n; i++) {
            const rowName = nodeNameMap.get(baseEventIds[i]) || `ID:${baseEventIds[i]}`;
            const display = truncateName(rowName, 20);
            const cells = [
                `<td style="font-size:10px;color:#333;text-align:right;padding-right:4px;white-space:nowrap;max-width:140px;overflow:hidden;text-overflow:ellipsis" title="${rowName.replace(/"/g, '&quot;')}">${display}</td>`,
            ];
            for (let j = 0; j < n; j++) {
                const val = matrix[i][j];
                const isDiag = i === j;
                const bg = isDiag ? '#e0e0e0' : getColor(val, maxVal);
                const fg = isDiag ? '#666' : getTextColor(val, maxVal);
                cells.push(
                    `<td data-row="${i}" data-col="${j}" style="width:${CELL_SIZE}px;height:${CELL_SIZE}px;min-width:${CELL_SIZE}px;text-align:center;font-size:9px;padding:0;border:1px solid #ccc;background:${bg};color:${fg};cursor:default">${val}</td>`
                );
            }
            bodyRows.push(`<tr>${cells.join('')}</tr>`);
        }
        rows.push(`<tbody>${bodyRows.join('')}</tbody>`);

        return rows.join('');
    }, [n, matrix, baseEventIds, nodeNameMap, maxVal]);

    const handleExportCSV = useCallback((): void => {
        const names = baseEventIds.map((id) => nodeNameMap.get(id) || `ID:${id}`);
        const escapeCSV = (s: string): string => `"${s.replace(/"/g, '""')}"`;
        const header = ['', ...names.map(escapeCSV)].join(',');
        const csvRows = baseEventIds.map((_, i) => {
            const cells = [escapeCSV(names[i]), ...matrix[i].map((v) => String(v))];
            return cells.join(',');
        });
        const csv = [header, ...csvRows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `co_occurrence_matrix_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [baseEventIds, nodeNameMap, matrix]);

    if (n === 0) {
        return (
            <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: 'divider', textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    No double failures — all cut sets are order 1.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                    Double Failure Matrix (diagonal = total appearances)
                </Typography>
                <Tooltip title="Export to CSV">
                    <IconButton size="small" onClick={handleExportCSV}>
                        <FileDownloadIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Box ref={containerRef} sx={{ overflow: 'auto', maxHeight: 500, position: 'relative' }}>
                <table
                    style={{ borderCollapse: 'collapse' }}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    dangerouslySetInnerHTML={{ __html: tableHtml }}
                />
                <div
                    ref={tooltipRef}
                    style={{
                        display: 'none',
                        position: 'absolute',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        color: '#fff',
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        zIndex: 10,
                    }}
                />
            </Box>
        </Box>
    );
};

export default CoOccurrenceHeatmap;
